// cloudflare api document
// https://developers.cloudflare.com/images/transform-images/transform-via-url/
export interface OptimizeImageOptions {
  width?: number;
  height?: number;
  quality?: number;
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
  /** 出力フォーマット。既定は 'auto'（Accept ヘッダに応じて AVIF/WebP を自動配信） */
  format?: 'auto' | 'avif' | 'webp' | 'json';
}

/** URL 生成に必要な runtimeConfig.public の最小形 */
interface ImageRuntimeConfigPublic {
  env: string;
  CLOUDFLARE_IMAGE_DOMAIN: string;
}

/**
 * 解決済みの runtimeConfig を使って最適化済み URL を組み立てる純粋関数。
 * inject を一切行わないため、computed など再評価がレンダリング外で走る箇所からでも安全に呼べる。
 * @param config - runtimeConfig.public（`useOptimizedImageUrl` が setup で解決して渡す）
 * @param originalUrl - 元画像 URL
 * @param options - 画像最適化オプション
 * @returns 最適化済み画像 URL
 */
export const buildOptimizedImageUrl = (config: ImageRuntimeConfigPublic, originalUrl: string, options: OptimizeImageOptions = {}): string => {
  if (!originalUrl || originalUrl === '' || config.env === 'local' || !config.CLOUDFLARE_IMAGE_DOMAIN) {
    return originalUrl;
  }

  try {
    // Validate option parameters
    if (options.width && options.width <= 0) {
      return originalUrl;
    }

    if (options.height && options.height <= 0) {
      return originalUrl;
    }

    if (options.quality && (options.quality < 1 || options.quality > 100)) {
      return originalUrl;
    }

    // Build parameters string.
    // - format: 既定で 'auto'（AVIF/WebP 自動配信で帯域削減）。呼び出し側が指定すれば上書き
    // - onerror=redirect: 変換失敗時は元画像へリダイレクト
    const allOptions = {
      format: 'auto',
      ...options,
      onerror: 'redirect',
    };

    const params = Object.entries(allOptions)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${value}`)
      .join(',');
    // Cloudflare 変換のソースは絶対 URL である必要がある。
    // 先頭スラッシュのパス（同一オリジンの静的アセット、例: /img/top/xxx.webp）は
    // domain を前置して絶対 URL 化する。microCMS / storage 等の絶対 URL はそのまま。
    // ※ 先頭スラッシュのまま連結すると `/cdn-cgi/image/params//img/...` と二重スラッシュになり 307 で壊れる
    // domain 末尾の `/` は除去しておく（env 側が末尾スラッシュ付きでも `${domain}/img/...` が `//` にならないように）
    const domain = config.CLOUDFLARE_IMAGE_DOMAIN.replace(/\/+$/, '');
    const source = originalUrl.startsWith('/') ? `${domain}${originalUrl}` : originalUrl;
    const generatedUrl = `${domain}/cdn-cgi/image/${params}/${source}`;
    return generatedUrl;
  } catch (error: unknown) {
    if (error instanceof Error) {
      return originalUrl;
    }
    return originalUrl;
  }
};
